import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShareNetwork, Prohibit, ClockClockwise, Key, CaretRight, Copy } from '@phosphor-icons/react';
import axios from 'axios';
import { shareApi, SHARE_STATUS } from './shareApi';
import { jstDate, jstDateTime } from '@/utils/jstDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

const Badge = ({ status }) => {
  const m = SHARE_STATUS[status] || SHARE_STATUS.expired;
  return <span className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold ${m.cls}`}>{m.label}</span>;
};

const Row = ({ s, onOpen }) => (
  <button type="button" onClick={() => onOpen(s.id)} data-testid={`share-row-${s.id}`}
    className="w-full text-left px-3 py-2.5 border-b border-zinc-200 hover:bg-zinc-50 flex items-center gap-3">
    <ShareNetwork size={15} weight="duotone" className="shrink-0 text-zinc-400" />
    <span className="text-[12px] font-semibold text-zinc-900 w-56 truncate">{s.recipient}</span>
    <span className="text-[11px] text-zinc-500 w-20">{s.documents?.length || 0}件</span>
    <span className="text-[11px] text-zinc-500 w-32 truncate">{s.created_by}</span>
    <span className="font-mono text-[11px] text-zinc-500 w-24">{jstDate(s.expires_at)}</span>
    <Badge status={s.status} />
    <span className="text-[11px] text-zinc-500 w-28 tabular-nums">
      閲覧{s.view_count} / DL{s.download_count}
    </span>
    <span className="font-mono text-[10px] text-zinc-400 w-32 truncate">
      {s.last_access_at ? jstDateTime(s.last_access_at) : '未アクセス'}
    </span>
    <CaretRight size={13} className="ml-auto shrink-0 text-zinc-300" />
  </button>
);

const KIND_LABEL = { open: 'リンクを開いた', view: '閲覧', download: 'ダウンロード',
  zip: 'ZIPダウンロード', password_failed: 'パスワード誤り' };

export default function SharesList() {
  const [data, setData] = useState(null);
  const [box, setBox] = useState(
    new URLSearchParams(window.location.search).get('box') === 'received' ? 'received' : 'external');
  const [received, setReceived] = useState([]);
  const [scope, setScope] = useState('mine');
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newPw, setNewPw] = useState('');

  const load = useCallback(async () => {
    try { setData(await shareApi.list(scope)); }
    catch (e) { toast.error(e?.response?.data?.detail || '共有を読めませんでした'); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (box !== 'received') return;
    axios.get(`${API}/internal-shares`, { params: { box: 'received' } })
      .then((r) => setReceived(r.data?.shares || [])).catch(() => setReceived([]));
  }, [box]);
  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    shareApi.detail(openId).then(setDetail).catch(() => setOpenId(null));
  }, [openId]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); setDetail(await shareApi.detail(openId)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'できませんでした'); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="dms-shares-page">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold text-zinc-900">共有</h1>
        <span className="inline-flex items-center gap-1 text-[10px]">
          <button type="button" onClick={() => setBox('external')} data-testid="shares-box-external"
            className={`px-2 py-0.5 rounded border ${box === 'external' ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>社外共有</button>
          <button type="button" onClick={() => setBox('received')} data-testid="shares-box-received"
            className={`px-2 py-0.5 rounded border ${box === 'received' ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>社内で受け取った共有</button>
        </span>
        {data?.can_see_all && (
          <span className="inline-flex items-center gap-1 text-[10px]">
            <button type="button" onClick={() => setScope('mine')} data-testid="shares-scope-mine"
              className={`px-2 py-0.5 rounded border ${scope === 'mine' ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>自分の共有</button>
            <button type="button" onClick={() => setScope('all')} data-testid="shares-scope-all"
              className={`px-2 py-0.5 rounded border ${scope === 'all' ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>全員の共有</button>
          </span>
        )}
        <span className="ml-auto text-[11px] text-zinc-400">{data?.total ?? 0}件</span>
      </div>

      {box === 'received' ? (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden"
          data-testid="shares-received-list">
          {received.length === 0 ? (
            <p className="px-3 py-8 text-center text-[12px] text-zinc-400">受け取った共有はありません</p>
          ) : received.map((r) => (
            <a key={r.id} href={r.open_path} data-testid={`received-share-${r.id}`}
              onClick={() => axios.post(`${API}/internal-shares/${r.id}/read`).catch(() => {})}
              className="flex items-center gap-3 px-3 py-2.5 border-b border-zinc-200 hover:bg-zinc-50">
              {!r.read_at && <span className="w-1.5 h-1.5 rounded-full bg-teal-600 shrink-0" />}
              <span className="text-[12px] text-zinc-900 truncate flex-1">{r.label}</span>
              <span className="text-[11px] text-zinc-500 w-32 truncate">{r.shared_by}</span>
              <span className="text-[11px] text-zinc-500 truncate max-w-[14rem]">{r.message}</span>
              <span className="font-mono text-[10px] text-zinc-400">{jstDateTime(r.created_at)}</span>
            </a>
          ))}
        </div>
      ) : !openId ? (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          {(data?.shares || []).length === 0 ? (
            <p className="px-3 py-8 text-center text-[12px] text-zinc-400">外部共有はまだありません</p>
          ) : data.shares.map((s) => <Row key={s.id} s={s} onOpen={setOpenId} />)}
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-200 bg-white">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200">
            <button type="button" onClick={() => { setOpenId(null); setNewPw(''); }}
              data-testid="share-detail-back"
              className="text-[11px] text-teal-700 hover:underline">← 一覧へ</button>
            <span className="text-[12px] font-semibold text-zinc-900">{detail?.recipient}</span>
            {detail && <Badge status={detail.status} />}
            <div className="ml-auto flex items-center gap-2">
              <button type="button" data-testid="share-extend-7"
                onClick={() => act(() => shareApi.extend(openId, 7), '期限を7日に延長しました')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-teal-500">
                <ClockClockwise size={11} />期限を延長（7日）
              </button>
              <button type="button" data-testid="share-reissue"
                onClick={() => act(async () => { const r = await shareApi.reissue(openId); setNewPw(r.password); },
                  'パスワードを再発行しました')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-teal-500">
                <Key size={11} />パスワード再発行
              </button>
              <button type="button" data-testid="share-revoke"
                onClick={() => act(() => shareApi.revoke(openId), '停止しました')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-200 text-[10px] text-rose-700 hover:bg-rose-50">
                <Prohibit size={11} />停止する
              </button>
            </div>
          </div>

          {newPw && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900">
              新しいパスワード: <code className="font-mono font-semibold" data-testid="share-new-password">{newPw}</code>
              <button type="button" onClick={() => navigator.clipboard.writeText(newPw)}
                className="inline-flex items-center gap-1 text-[10px] text-amber-800 underline"><Copy size={10} />コピー</button>
              <span className="text-amber-700">この画面を閉じると二度と表示されません</span>
            </div>
          )}

          <div className="px-3 py-2 text-[11px] text-zinc-600 space-y-1 border-b border-zinc-100">
            <p>リンク: <code className="font-mono text-[10px]">{window.location.origin}{detail?.url_path}</code></p>
            <p>期限 {jstDate(detail?.expires_at)} ・
              {detail?.allow_download ? ' ダウンロード可' : ' 閲覧のみ'}
              {detail?.max_downloads ? ` ・ 上限${detail.max_downloads}回（${detail.download_count}回済）` : ''}
              {detail?.include_confidential ? ' ・ 社外秘を含む' : ''}</p>
            <p className="text-zinc-400">{detail?.note}</p>
          </div>

          <ul className="px-3 py-2 space-y-1 border-b border-zinc-100">
            {(detail?.documents || []).map((m) => (
              <li key={m.document_id} className="flex items-center gap-2 text-[11px] text-zinc-700">
                <span className="font-mono text-[10px] text-zinc-400 w-24 truncate">{m.document_number}</span>
                <span className="truncate">{m.title}</span>
              </li>
            ))}
          </ul>

          <div className="px-3 py-2">
            <p className="text-[10px] text-zinc-400 mb-1">アクセス履歴</p>
            {(detail?.access_log || []).length === 0 ? (
              <p className="text-[11px] text-zinc-400">まだアクセスはありません</p>
            ) : (
              <ul className="space-y-0.5 max-h-72 overflow-y-auto" data-testid="share-access-log">
                {detail.access_log.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-[10px] text-zinc-600">
                    <span className="font-mono w-32">{jstDateTime(a.at)}</span>
                    <span className="w-28">{KIND_LABEL[a.kind] || a.kind}</span>
                    <span className="font-mono w-32 truncate">{a.ip}</span>
                    <span className="truncate">{a.filename || ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
